import { useEffect, useState, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { useRunRoutine, useRecordRoutineRun } from "../../hooks/use-routines";
import { formatRoutineProcedure } from "../../lib/routine-run";
import { modalOverlay, modalBox, labelStyle, inputStyle } from "../scope/task-modal-shared";
import type { CompiledRoutineProcedure, Routine, RoutineRunOutcome } from "../../../lib/types";

interface RunRoutineModalProps {
  open: boolean;
  onClose: () => void;
  routine: Routine;
}

const OUTCOMES: { value: RoutineRunOutcome; label: string }[] = [
  { value: "success", label: "Success" },
  { value: "partial", label: "Partial" },
  { value: "error", label: "Error" },
];

/**
 * Sprint 26.1 — the federated Run flow (Decisions §3).
 *
 * "Run now" → `run_routine` compiles the procedure (in-progress → result).
 * Kuvio executes nothing, so this is the DEGRADED path by construction: we
 * surface the compiled procedure (copyable) for the user to run in their host,
 * then let them record the outcome — which writes `record_routine_run` AND a
 * `create_activity` row (host-side, no double-log) so the run surfaces in the
 * Activity tab, Overview "Recently", and per-routine run history.
 *
 * The flow MOUNTS on open (`{open && …}`), so each run starts from fresh state
 * with no reset-in-effect; the only effect kicks off `run_routine`, setting
 * state from its async callbacks.
 */
export function RunRoutineModal({ open, onClose, routine }: RunRoutineModalProps) {
  if (!open) return null;
  return <RunRoutineFlow onClose={onClose} routine={routine} />;
}

function RunRoutineFlow({ onClose, routine }: { onClose: () => void; routine: Routine }) {
  const runRoutine = useRunRoutine();
  const recordRun = useRecordRoutineRun();

  const [procedure, setProcedure] = useState<CompiledRoutineProcedure | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<RoutineRunOutcome>("success");
  const [summary, setSummary] = useState("");
  const [recorded, setRecorded] = useState(false);

  // Compile on mount. Kuvio never executes — run_routine just returns the
  // procedure; state is set only from the async callbacks.
  useEffect(() => {
    runRoutine.mutate(routine.id, {
      onSuccess: (p) => setProcedure(p),
      onError: (e) => setCompileError(e instanceof Error ? e.message : "Couldn't compile this routine"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routine.id]);

  function copyProcedure() {
    if (!procedure) return;
    navigator.clipboard?.writeText(formatRoutineProcedure(procedure)).then(
      () => showToast("Host-ready procedure copied — run it in your host"),
      () => showToast("Couldn't copy to clipboard", "error", true),
    );
  }

  function recordOutcome() {
    // record_routine_run records telemetry AND writes the run's Activity row
    // server-side (Sprint 26.1.1) — no separate create_activity call, so the run
    // is logged exactly once and shows in Activity / Recently / run history.
    recordRun.mutate(
      { id: routine.id, outcome, summary: summary.trim() || undefined, last_run_by: "console" },
      {
        onSuccess: () => {
          setRecorded(true);
          showToast("Run recorded");
        },
        onError: (e) => showToast(`Couldn't record run: ${e instanceof Error ? e.message : "failed"}`, "error", true),
      },
    );
  }

  const compiling = runRoutine.isPending || (!procedure && !compileError);
  const recording = recordRun.isPending;

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label={`Run routine ${routine.name}`} data-testid="run-routine-modal">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={iconBox}>
            <i className="ti ti-player-play" aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>Run {routine.name}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{routine.purpose}</div>
          </div>
        </div>

        {/* In-progress */}
        {compiling && (
          <div style={infoBlock} data-testid="run-routine-compiling">
            <i className="ti ti-loader-2" style={{ fontSize: 14, animation: "spin 0.8s linear infinite" }} aria-hidden="true" /> Compiling procedure…
          </div>
        )}

        {/* Compile error (disabled / archived / not found) */}
        {!compiling && compileError && (
          <div style={{ ...infoBlock, color: "var(--c-red-600)" }} data-testid="run-routine-error">
            <i className="ti ti-alert-triangle" style={{ fontSize: 14 }} aria-hidden="true" /> {compileError}
          </div>
        )}

        {/* Result — the compiled procedure (degraded path: surface it for the user to run). */}
        {!compiling && procedure && (
          <>
            <div style={degradedBanner} data-testid="run-routine-degraded">
              <i className="ti ti-info-circle" style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <span>
                Kuvio doesn't execute routines — run this procedure in your host, then record the outcome below so it
                shows in Activity.
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "14px 0 4px" }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Procedure</label>
              <button type="button" onClick={copyProcedure} style={copyBtn} title="Copy a host-ready procedure">
                <i className="ti ti-copy" style={{ fontSize: 12 }} aria-hidden="true" /> Copy
              </button>
            </div>
            <pre style={procedureBox} data-testid="run-routine-procedure">{formatRoutineProcedure(procedure)}</pre>

            {recorded ? (
              <div style={recordedBlock} data-testid="run-routine-recorded">
                <i className="ti ti-circle-check" style={{ fontSize: 15, color: "var(--c-teal-600)" }} aria-hidden="true" />
                <span>Run recorded — it now appears in this routine's history and the agent's Activity.</span>
              </div>
            ) : (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: "0.5px solid var(--color-border-tertiary)" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 8 }}>
                  Record the outcome
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  {OUTCOMES.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setOutcome(o.value)}
                      style={outcomeChip(outcome === o.value, o.value)}
                    >
                      {outcome === o.value && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />} {o.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  style={inputStyle}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="One-line summary — e.g. 3 invoices reconciled"
                  maxLength={200}
                />
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
            {recorded ? "Done" : "Close"}
          </button>
          {!compiling && procedure && !recorded && (
            <button
              type="button"
              data-variant="primary"
              onClick={recordOutcome}
              disabled={recording}
              style={{ fontSize: 13, padding: "8px 14px" }}
              data-testid="run-routine-record"
            >
              {recording ? "Recording…" : "Mark as run"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 18,
  flexShrink: 0,
};

const infoBlock: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "var(--color-text-secondary)",
  padding: "12px 0",
};

const degradedBanner: CSSProperties = {
  display: "flex",
  gap: 8,
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 12px",
};

const procedureBox: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--color-text-primary)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: 12,
  maxHeight: 220,
  overflowY: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "var(--font-mono, monospace)",
};

const copyBtn: CSSProperties = {
  fontSize: 12,
  padding: "3px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const recordedBlock: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  color: "var(--color-text-secondary)",
  marginTop: 16,
  paddingTop: 16,
  borderTop: "0.5px solid var(--color-border-tertiary)",
};

const OUTCOME_COLOR: Record<RoutineRunOutcome, string> = {
  success: "var(--c-teal-600)",
  partial: "var(--c-amber-600)",
  error: "var(--c-red-600)",
};

const outcomeChip = (on: boolean, value: RoutineRunOutcome): CSSProperties => ({
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: "var(--border-radius-pill, 999px)",
  border: on ? `1px solid ${OUTCOME_COLOR[value]}` : "var(--kuvio-border-default)",
  background: on ? OUTCOME_COLOR[value] : "var(--color-background-primary)",
  color: on ? "#fff" : "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});
